import React, { useState, useEffect } from "react";
import WhatsAppVoicePlayer from "./WhatsAppVoicePlayer";
import MessageContextMenu from "./MessageContextMenu";
import MessageReactionBar from "./MessageReactionBar";
import ExtendedEmojiModal from "./ExtendedEmojiModal";
import { getImageUrl } from "../../config/api";

export default function MessageTimeline({
  messages = [],
  loading,
  hasLoaded = false,
  messagesCanvasRef,
  messagesEndRef,
  onInsertQuickReply,
  onOpenTemplateModal,
  inChatSearch = "",
  onScrollToBottom,
  onRetryMessage,
  onInitiateReply,
  onReact,
  onToggleStar,
  onTogglePin,
  onCopyText,
  providerLabel = "Interakt Connected",
}) {
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [activeMenuMsg, setActiveMenuMsg] = useState(null);
  const [extendedEmojiMsg, setExtendedEmojiMsg] = useState(null);
  const [viewOnceNoticeMsg, setViewOnceNoticeMsg] = useState(null);

  // Close lightbox on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setLightboxMedia(null);
        setActiveMenuMsg(null);
        setExtendedEmojiMsg(null);
        setViewOnceNoticeMsg(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleCanvasScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    setShowScrollBottom(distanceToBottom > 100);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDateLabel = (ts) => {
    if (!ts) return "";
    const msgDate = new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (msgDate.toDateString() === today.toDateString()) return "TODAY";
    if (msgDate.toDateString() === yesterday.toDateString()) return "YESTERDAY";
    return msgDate.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).toUpperCase();
  };

  // Helper to parse template metadata from message content
  const parseTemplateContent = (body) => {
    if (!body) return { isParsed: false, templateName: "", cleanBody: "" };
    const match = body.match(/^\[Template:\s*([^\]]+)\]\s*(.*)$/s);
    if (match) {
      return {
        isParsed: true,
        templateName: match[1].trim(),
        cleanBody: match[2].trim(),
      };
    }
    return { isParsed: false, templateName: "", cleanBody: body };
  };

  // Check if message text is just an auto placeholder (e.g. "📷 Photo")
  const isImagePlaceholder = (text) => {
    if (!text) return true;
    const clean = text.trim();
    return (
      clean === "📷 Photo" ||
      clean === "Photo" ||
      clean === "📷" ||
      clean === "[Photo]" ||
      clean === "[Image]" ||
      clean === "Image" ||
      clean === "[UNSUPPORTED]"
    );
  };

  // Check if message text is just an auto placeholder (e.g. "📹 Video")
  const isVideoPlaceholder = (text) => {
    if (!text) return true;
    const clean = text.trim();
    return (
      clean === "📹 Video" ||
      clean === "Video" ||
      clean === "📹" ||
      clean === "[Video]" ||
      clean === "[UNSUPPORTED]"
    );
  };

  // Extract clean filename from media_url or message for document cards
  const getDocDisplayName = (msg) => {
    if (!msg) return "Document Attachment";
    if (msg.media_url && typeof msg.media_url === "string") {
      try {
        const clean = msg.media_url.split("?")[0].split("#")[0];
        const basename = clean.substring(clean.lastIndexOf("/") + 1);
        if (basename && basename.length > 3 && basename.includes(".")) {
          return decodeURIComponent(basename);
        }
      } catch {}
    }
    if (msg.message_body && msg.message_body.startsWith("📄")) {
      return msg.message_body.replace(/^📄\s*/, "").trim();
    }
    return "Document Attachment.pdf";
  };

  // Robust media URL resolution supporting base64, live host, relative, and API base
  const resolveMediaSrc = (msg) => {
    if (!msg) return null;
    // 1. Direct base64 data URL stored in database (instant, zero-latency)
    if (msg.media_data && typeof msg.media_data === "string" && msg.media_data.startsWith("data:")) {
      return msg.media_data;
    }
    // 2. Already a data URL in media_url
    if (msg.media_url && typeof msg.media_url === "string" && msg.media_url.startsWith("data:")) {
      return msg.media_url;
    }
    // 3. Absolute http / https URL
    if (msg.media_url && (msg.media_url.startsWith("http://") || msg.media_url.startsWith("https://"))) {
      return msg.media_url;
    }
    // 4. Stored local path (e.g. /uploads/whatsapp_media/...)
    if (msg.media_url && typeof msg.media_url === "string") {
      const cleanPath = msg.media_url.startsWith("/") ? msg.media_url : `/${msg.media_url}`;
      return getImageUrl(cleanPath);
    }
    return null;
  };

  // Filter messages if search query exists
  const searchQuery = inChatSearch.trim().toLowerCase();
  const displayedMessages = searchQuery
    ? messages.filter((m) => m.message_body?.toLowerCase().includes(searchQuery))
    : messages;

  const pinnedMessages = messages.filter((m) => m.is_pinned && !m.is_deleted);
  const latestPinned = pinnedMessages.length > 0 ? pinnedMessages[pinnedMessages.length - 1] : null;

  const scrollToMessage = (msgId) => {
    const el = document.getElementById(`wa-msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("wa-bubble-highlight");
      setTimeout(() => el.classList.remove("wa-bubble-highlight"), 2500);
    }
  };

  return (
    <div
      ref={messagesCanvasRef}
      onScroll={handleCanvasScroll}
      className="wa-timeline-canvas"
      role="log"
      aria-live="polite"
    >
      {/* Authentic WhatsApp Pinned Message Banner */}
      {latestPinned && (
        <div
          className="wa-pinned-banner"
          onClick={() => scrollToMessage(latestPinned.id)}
          title="Click to jump to pinned message"
          role="button"
          tabIndex={0}
        >
          <div className="wa-pinned-accent-line"></div>
          <div className="wa-pinned-banner-content">
            <div className="wa-pinned-header">
              <i className="bi bi-pin-fill wa-pinned-icon"></i>
              <span className="wa-pinned-author">
                {latestPinned.direction === "outbound" ? "You" : latestPinned.sender_name || "Customer"}
              </span>
              <span className="wa-pinned-tag">• Pinned message</span>
            </div>
            <div className="wa-pinned-text text-truncate">
              {latestPinned.message_body || (latestPinned.media_url ? "[Media Attachment]" : "Pinned message")}
            </div>
          </div>
          <button
            type="button"
            className="wa-pinned-unpin-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onTogglePin) onTogglePin(latestPinned);
            }}
            title="Unpin message"
            aria-label="Unpin message"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}
      {/* Floating Scroll to Latest Button */}
      {showScrollBottom && (
        <button
          type="button"
          className="wa-btn-scroll-bottom"
          onClick={() => {
            if (onScrollToBottom) {
              onScrollToBottom(true);
            } else if (messagesCanvasRef.current) {
              messagesCanvasRef.current.scrollTo({
                top: messagesCanvasRef.current.scrollHeight,
                behavior: "smooth",
              });
            }
          }}
          title="Scroll to latest"
        >
          <i className="bi bi-chevron-down"></i>
        </button>
      )}

      {loading || (!hasLoaded && messages.length === 0) ? (
        <div className="wa-timeline-loading">
          <div className="spinner-border spinner-border-sm text-success me-2" role="status"></div>
          <span>Loading chat history...</span>
        </div>
      ) : messages.length === 0 ? (
        <div className="wa-timeline-empty-state">
          <div className="wa-empty-art">
            <i className="bi bi-chat-heart-fill"></i>
          </div>
          <h5>No messages yet with this contact</h5>
          <p>
            Start the conversation by sending an approved greeting or dispatch update.
          </p>
          <div className="wa-empty-actions">
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() =>
                onInsertQuickReply("🙏 Namaskaram! Greetings from Sri VaithikaSri Publications.")
              }
            >
              Insert Namaskaram Greeting
            </button>
            <button
              type="button"
              className="btn btn-sm btn-success text-white"
              onClick={onOpenTemplateModal}
            >
              <i className="bi bi-lightning-charge-fill me-1"></i> Send Template
            </button>
          </div>
        </div>
      ) : (
        <div className="wa-timeline-bubbles">
          {/* In-Thread Search Feedback Banner */}
          {searchQuery && (
            <div className="wa-search-banner">
              <i className="bi bi-search me-1"></i>
              <span>
                Found <strong>{displayedMessages.length}</strong> message{displayedMessages.length === 1 ? "" : "s"} matching "<em>{inChatSearch}</em>"
              </span>
            </div>
          )}

          {/* Security Notice */}
          <div className="wa-encryption-pill">
            <i className="bi bi-lock-fill me-1"></i>
            <span>
              Messages are encrypted & routed directly through {providerLabel?.toLowerCase().includes("interakt") ? "Interakt WhatsApp API" : "Meta WhatsApp Cloud API"}
            </span>
          </div>

          {displayedMessages.map((m, idx) => {
            const isSystem = m.direction === "system" || m.message_type === "system";
            const isOutbound = m.direction === "outbound";
            const isTemplate =
              m.message_type === "template" || m.message_body?.startsWith("[Template:");
            const isFailed = m.status === "failed";
            const timeStr = formatTime(m.created_at);
            const tplData = isTemplate ? parseTemplateContent(m.message_body) : null;

            // Media Detection
            const mime = m.mime_type || "";
            const type = m.message_type || "";
            const isUnsupported =
              type === "unsupported" ||
              m.message_body === "[UNSUPPORTED]" ||
              m.message_body?.includes("Unsupported message") ||
              m.message_body?.includes("View-Once") ||
              m.message_body?.includes("View Once");
            const isImage =
              !isUnsupported && (
                type === "image" ||
                mime.startsWith("image/") ||
                (typeof m.media_url === "string" && /\.(jpe?g|png|webp|gif)$/i.test(m.media_url)) ||
                (typeof m.media_data === "string" && m.media_data.startsWith("data:image/"))
              );
            const isVideo =
              !isUnsupported && (
                type === "video" ||
                mime.startsWith("video/") ||
                (typeof m.media_url === "string" && /\.(mp4|mov|webm)$/i.test(m.media_url))
              );
            const isAudio =
              !isUnsupported && (
                type === "audio" ||
                type === "voice" ||
                mime.startsWith("audio/") ||
                (typeof m.media_url === "string" && /\.(ogg|mp3|wav|m4a|aac)$/i.test(m.media_url))
              );
            const isDoc =
              !isUnsupported && (
                type === "document" ||
                mime === "application/pdf" ||
                (typeof m.media_url === "string" && /\.pdf$/i.test(m.media_url))
              );

            const mediaSrc = resolveMediaSrc(m);
            const hasRealImageCaption = isImage && !isImagePlaceholder(m.message_body) && !tplData?.isParsed;
            const hasRealVideoCaption = isVideo && !isVideoPlaceholder(m.message_body) && !tplData?.isParsed;
            const hasRealCaption = hasRealImageCaption || hasRealVideoCaption || (!isImage && !isVideo && !isImagePlaceholder(m.message_body) && !isVideoPlaceholder(m.message_body));
            const isPureImageBubble = isImage && !hasRealImageCaption && !isTemplate;
            const isPureVideoBubble = isVideo && !hasRealVideoCaption && !isTemplate;
            const isPureMediaBubble = isPureImageBubble || isPureVideoBubble;

            // Date separator
            const prevMsg = displayedMessages[idx - 1];
            const showDate =
              !prevMsg ||
              new Date(m.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

            if (isSystem) {
              return (
                <React.Fragment key={m.id || idx}>
                  {showDate && (
                    <div className="wa-date-divider" role="separator">
                      <span>{formatDateLabel(m.created_at)}</span>
                    </div>
                  )}
                  <div className="wa-bubble-row system">
                    <div className="wa-bubble system">
                      <span className="wa-msg-text">{m.message_body}</span>
                      <span className="wa-msg-time">{timeStr}</span>
                    </div>
                  </div>
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={m.id || idx}>
                {showDate && (
                  <div className="wa-date-divider" role="separator">
                    <span>{formatDateLabel(m.created_at)}</span>
                  </div>
                )}

                <div
                  id={`wa-msg-${m.id}`}
                  className={`wa-bubble-row ${isOutbound ? "outbound" : "inbound"} ${m.is_deleted ? "is-deleted" : ""}`}
                  onMouseEnter={() => setHoveredMsgId(m.id)}
                  onMouseLeave={() => {
                    if (hoveredMsgId === m.id) setHoveredMsgId(null);
                  }}
                >
                  <div
                    className={`wa-bubble ${isOutbound ? "outbound" : "inbound"} ${isFailed ? "failed" : ""} ${
                      isAudio ? "voice-bubble" : ""
                    } ${isPureImageBubble ? "wa-image-bubble" : isImage ? "wa-image-with-caption" : ""} ${
                      isPureVideoBubble ? "wa-video-bubble" : isVideo ? "wa-video-with-caption" : ""
                    } ${m.is_deleted ? "wa-bubble-deleted" : ""}`}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setActiveMenuMsg({
                        message: m,
                        position: { x: e.clientX, y: e.clientY },
                      });
                    }}
                  >
                    {/* Floating Reaction Bar on Hover (Authentic WhatsApp Web) */}
                    {hoveredMsgId === m.id && !m.is_deleted && (
                      <MessageReactionBar
                        onSelectEmoji={(emoji) => onReact && onReact(m, emoji)}
                        onOpenMore={() => setExtendedEmojiMsg(m)}
                        className={isOutbound ? "wa-reaction-bar-outbound" : "wa-reaction-bar-inbound"}
                      />
                    )}

                    {/* Chevron Dropdown Trigger Button */}
                    <button
                      type="button"
                      className={`wa-bubble-chevron-btn ${
                        hoveredMsgId === m.id || activeMenuMsg?.message?.id === m.id ? "visible" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActiveMenuMsg({
                          message: m,
                          position: { x: rect.right - 180, y: rect.bottom + 4 },
                        });
                      }}
                      title="Message options"
                      aria-label="Message options"
                    >
                      <i className="bi bi-chevron-down"></i>
                    </button>

                    {/* Quoted Message Card (if message is a reply to another message) */}
                    {m.reply_to_body && !m.is_deleted && (
                      <div className="wa-quoted-reply-card">
                        <div className="wa-quoted-accent-line"></div>
                        <div className="wa-quoted-inner-content">
                          <div className="wa-quoted-author">
                            {m.reply_to_sender || (isOutbound ? "You" : "Customer")}
                          </div>
                          <div className="wa-quoted-text text-truncate">
                            {m.reply_to_body}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* If Deleted: Render Authentic Deleted Tombstone */}
                    {m.is_deleted ? (
                      <div className="wa-msg-deleted-tombstone">
                        <i className="bi bi-slash-circle me-1"></i>
                        <span className="fst-italic">
                          {m.message_body || (isOutbound ? "You deleted this message" : "This message was deleted")}
                        </span>
                      </div>
                    ) : (
                      <>
                        {/* Template Badge Header */}
                        {isTemplate && (
                          <div className="wa-bubble-template-header">
                            <i className="bi bi-patch-check-fill me-1"></i>
                            <span>Approved Meta Template {tplData?.templateName ? `• ${tplData.templateName}` : ""}</span>
                          </div>
                        )}

                    {/* ========================================================
                        VIEW-ONCE / UNSUPPORTED MEDIA CARD (Authentic WhatsApp Web)
                        ======================================================== */}
                    {isUnsupported && (
                      <div
                        className="wa-viewonce-card"
                        onClick={() => setViewOnceNoticeMsg(m)}
                        role="button"
                        tabIndex={0}
                        title="View-once photo • Click for details"
                      >
                        <div className="wa-viewonce-badge">
                          <span className="wa-viewonce-num">1</span>
                        </div>
                        <div className="wa-viewonce-meta">
                          <div className="wa-viewonce-title">
                            {m.message_body?.toLowerCase().includes("video") ? "View once video" : "View once photo"}
                          </div>
                          <div className="wa-viewonce-subtitle">
                            <i className="bi bi-shield-lock-fill me-1"></i>
                            <span>View on WhatsApp mobile</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ========================================================
                        AUTHENTIC WHATSAPP PHOTO / IMAGE RENDERER
                        ======================================================== */}
                    {isImage && (
                      <div className="wa-photo-container">
                        {mediaSrc ? (
                          <div
                            className="wa-photo-wrapper"
                            onClick={() =>
                              setLightboxMedia({
                                src: mediaSrc,
                                caption: hasRealCaption ? m.message_body : "",
                                senderName: m.sender_name || (isOutbound ? "You" : "Customer"),
                                time: timeStr,
                              })
                            }
                            title="Click to view full photo"
                          >
                            <img
                              className="wa-photo-img"
                              src={mediaSrc}
                              alt={hasRealCaption ? m.message_body : "WhatsApp Photo"}
                              loading="lazy"
                              onError={(e) => {
                                // Fallback to base64 if available
                                if (m.media_data && e.target.src !== m.media_data) {
                                  e.target.src = m.media_data;
                                }
                              }}
                            />
                            {/* Overlay Time Pill for Pure Photo Bubbles (Authentic WhatsApp Web) */}
                            {isPureImageBubble && (
                              <div className="wa-media-time-pill" aria-label={`Sent at ${timeStr}`}>
                                {m.is_pinned && <i className="bi bi-pin-fill text-success me-1 wa-pin-icon" title="Pinned message"></i>}
                                {m.is_starred && <i className="bi bi-star-fill text-warning me-1 wa-star-icon" title="Starred"></i>}
                                <span className="wa-msg-time">{timeStr}</span>
                                {isOutbound && (
                                  <span className="wa-msg-status">
                                    {isFailed ? (
                                      <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                    ) : m.status === "read" ? (
                                      <i className="bi bi-check2-all wa-tick-read"></i>
                                    ) : m.status === "delivered" ? (
                                      <i className="bi bi-check2-all wa-tick-delivered"></i>
                                    ) : (
                                      <i className="bi bi-check2 wa-tick-sent"></i>
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          // Pending / downloading state
                          <div className="wa-photo-placeholder">
                            <div className="spinner-border spinner-border-sm text-secondary me-2" role="status"></div>
                            <span className="text-muted small">Loading photo...</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ========================================================
                        AUTHENTIC WHATSAPP VIDEO RENDERER
                        ======================================================== */}
                    {isVideo && (
                      <div className="wa-video-container">
                        {mediaSrc ? (
                          <div className="wa-video-wrapper">
                            <video
                              className="v-media-video"
                              controls
                              playsInline
                              preload="metadata"
                              src={mediaSrc}
                              title={hasRealVideoCaption ? m.message_body : "Video message"}
                              onError={(e) => {
                                // Fallback to base64 if available
                                if (m.media_data && e.target.src !== m.media_data) {
                                  e.target.src = m.media_data;
                                }
                              }}
                            >
                              Your browser does not support video playback.
                            </video>
                            {/* Overlay Time Pill for Pure Video Bubbles (Authentic WhatsApp Web) */}
                            {isPureVideoBubble && (
                              <div className="wa-media-time-pill" aria-label={`Sent at ${timeStr}`}>
                                {m.is_pinned && <i className="bi bi-pin-fill text-success me-1 wa-pin-icon" title="Pinned message"></i>}
                                {m.is_starred && <i className="bi bi-star-fill text-warning me-1 wa-star-icon" title="Starred"></i>}
                                <span className="wa-msg-time">{timeStr}</span>
                                {isOutbound && (
                                  <span className="wa-msg-status">
                                    {isFailed ? (
                                      <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                    ) : m.status === "sending..." || m.status === "pending" ? (
                                      <i className="bi bi-clock wa-tick-pending" title="Sending..."></i>
                                    ) : m.status === "read" ? (
                                      <i className="bi bi-check2-all wa-tick-read"></i>
                                    ) : m.status === "delivered" ? (
                                      <i className="bi bi-check2-all wa-tick-delivered"></i>
                                    ) : (
                                      <i className="bi bi-check2 wa-tick-sent"></i>
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="wa-photo-placeholder">
                            <div className="spinner-border spinner-border-sm text-secondary me-2" role="status"></div>
                            <span className="text-muted small">Loading video...</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ========================================================
                        VOICE NOTE / AUDIO PLAYER
                        ======================================================== */}
                    {isAudio && (
                      <div className="wa-audio-container">
                        {mediaSrc ? (
                          <WhatsAppVoicePlayer
                            src={mediaSrc}
                            senderName={m.sender_name || (isOutbound ? "You" : "Customer")}
                            isOutbound={isOutbound}
                          />
                        ) : (
                          <div className="v-media-pending">
                            <div className="spinner-border spinner-border-sm text-secondary me-2" role="status"></div>
                            <span className="text-muted small">Voice note downloading...</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ========================================================
                        DOCUMENT / PDF RENDERER (Authentic WhatsApp Web Card)
                        ======================================================== */}
                    {isDoc && !isImage && (
                      <div className="v-media-doc-card">
                        <div className="v-media-icon">
                          <i className={`bi ${mime === "application/pdf" || (m.media_url && m.media_url.endsWith(".pdf")) ? "bi-file-earmark-pdf-fill" : "bi-file-earmark-fill"}`}></i>
                        </div>
                        <div className="v-media-meta">
                          <span className="v-media-filename" title={getDocDisplayName(m)}>
                            {getDocDisplayName(m)}
                          </span>
                          <span className="v-media-desc">
                            {mime === "application/pdf" || (m.media_url && m.media_url.endsWith(".pdf")) ? "PDF Document" : mime || "Attachment"}
                          </span>
                        </div>
                        {mediaSrc && (
                          <a
                            href={mediaSrc}
                            target="_blank"
                            rel="noreferrer"
                            className="v-media-download"
                            title="Download document"
                            download
                          >
                            <i className="bi bi-arrow-down-circle"></i>
                          </a>
                        )}
                      </div>
                    )}

                    {/* ========================================================
                        MESSAGE TEXT / CAPTION CONTENT
                        Hidden for view-once, pure photo bubbles, pure video bubbles, and voice notes
                        ======================================================== */}
                    {!isUnsupported && !isAudio && ((!isImage && !isVideo) || (isImage && hasRealImageCaption) || (isVideo && hasRealVideoCaption) || tplData?.isParsed) && (
                      <div className={`wa-msg-text ${(isImage && hasRealImageCaption) || (isVideo && hasRealVideoCaption) ? "wa-image-caption" : ""}`}>
                        {tplData?.isParsed ? tplData.cleanBody : m.message_body}
                      </div>
                    )}
                    </>
                    )}

                    {/* Reactions Pill Attached to Bottom Border of Bubble (Authentic WhatsApp Web) */}
                    {Array.isArray(m.reactions) && m.reactions.length > 0 && !m.is_deleted && (
                      <div
                        className="wa-bubble-reaction-pill"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onReact) onReact(m, m.reactions[0]?.emoji || "👍");
                        }}
                        title={m.reactions.map((r) => `${r.emoji} by ${r.by || "User"}`).join(", ")}
                      >
                        {Array.from(new Set(m.reactions.map((r) => r.emoji))).map((emoji, rxIdx) => (
                          <span key={rxIdx} className="wa-rx-item">{emoji}</span>
                        ))}
                        {m.reactions.length > 1 && (
                          <span className="wa-rx-count">{m.reactions.length}</span>
                        )}
                      </div>
                    )}

                    {/* ========================================================
                        FOOTER: TIMESTAMP & READ TICKS
                        (Rendered at bottom of bubble if NOT a pure photo/video bubble)
                        ======================================================== */}
                    {!isPureMediaBubble && (
                      <div className="wa-bubble-meta">
                        {m.is_pinned && (
                          <i
                            className="bi bi-pin-fill text-success me-1 wa-pin-icon"
                            title="Pinned message"
                          ></i>
                        )}
                        {m.is_starred && (
                          <i className="bi bi-star-fill text-warning me-1 wa-star-icon" title="Starred"></i>
                        )}
                        <span className="wa-msg-time">{timeStr}</span>

                        {isOutbound && (
                          <span className="wa-msg-status">
                            {isFailed ? (
                              <span className="wa-tick-failed" title={m.error_details || "Failed to deliver"}>
                                <i className="bi bi-exclamation-circle-fill me-1"></i>
                                {onRetryMessage && (
                                  <button
                                    type="button"
                                    className="wa-btn-retry"
                                    onClick={() => onRetryMessage(m)}
                                    title={m.error_details ? `Error: ${m.error_details}. Click to retry.` : "Retry send"}
                                  >
                                    Retry
                                  </button>
                                )}
                              </span>
                            ) : m.status === "sending..." || m.status === "pending" ? (
                              <i className="bi bi-clock wa-tick-pending" title="Sending..."></i>
                            ) : m.status === "read" ? (
                              <i className="bi bi-check2-all wa-tick-read" title="Read by customer"></i>
                            ) : m.status === "delivered" ? (
                              <i className="bi bi-check2-all wa-tick-delivered" title="Delivered"></i>
                            ) : (
                              <i className="bi bi-check2 wa-tick-sent" title="Sent"></i>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* ========================================================
          WHATSAPP AUTHENTIC FULLSCREEN PHOTO LIGHTBOX MODAL
          ======================================================== */}
      {lightboxMedia && (
        <div
          className="wa-lightbox-overlay"
          onClick={() => setLightboxMedia(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Photo Preview"
        >
          <div className="wa-lightbox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-lightbox-header">
              <div className="wa-lightbox-title">
                <i className="bi bi-image text-white me-2"></i>
                <span className="wa-lightbox-sender">{lightboxMedia.senderName}</span>
                <span className="wa-lightbox-time">• {lightboxMedia.time}</span>
              </div>
              <div className="wa-lightbox-actions">
                <a
                  href={lightboxMedia.src}
                  download={`vaithikasri_photo_${Date.now()}.jpg`}
                  className="wa-lightbox-btn"
                  title="Download photo"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-download"></i>
                </a>
                <button
                  type="button"
                  className="wa-lightbox-btn"
                  onClick={() => setLightboxMedia(null)}
                  title="Close preview (Esc)"
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <div className="wa-lightbox-content">
              <img
                src={lightboxMedia.src}
                alt={lightboxMedia.caption || "Full resolution photo"}
                className="wa-lightbox-image"
              />
            </div>

            {lightboxMedia.caption && (
              <div className="wa-lightbox-caption">
                <p>{lightboxMedia.caption}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          AUTHENTIC WHATSAPP CONTEXT MENU DROPDOWN
          ======================================================== */}
      <MessageContextMenu
        isOpen={Boolean(activeMenuMsg)}
        message={activeMenuMsg?.message}
        anchorPosition={activeMenuMsg?.position}
        onClose={() => setActiveMenuMsg(null)}
        onReply={(msg) => onInitiateReply && onInitiateReply(msg)}
        onReact={(msg) => setExtendedEmojiMsg(msg)}
        onCopy={(text) => onCopyText && onCopyText(text)}
        onStar={(msg) => onToggleStar && onToggleStar(msg)}
        onPin={(msg) => onTogglePin && onTogglePin(msg)}
      />

      {/* ========================================================
          EXTENDED EMOJI REACTION PICKER MODAL
          ======================================================== */}
      <ExtendedEmojiModal
        isOpen={Boolean(extendedEmojiMsg)}
        onClose={() => setExtendedEmojiMsg(null)}
        onSelectEmoji={(emoji) => {
          if (extendedEmojiMsg && onReact) {
            onReact(extendedEmojiMsg, emoji);
          }
        }}
      />

      {/* ========================================================
          VIEW-ONCE / PRIVACY EXPLANATION MODAL (Authentic WhatsApp Web)
          ======================================================== */}
      {viewOnceNoticeMsg && (
        <div
          className="wa-modal-backdrop"
          onClick={() => setViewOnceNoticeMsg(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="wa-viewonce-modal-title"
        >
          <div className="wa-viewonce-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="wa-viewonce-modal-top">
              <div className="wa-viewonce-modal-badge">
                <span className="wa-viewonce-circle-large">1</span>
              </div>
              <button
                type="button"
                className="wa-modal-close-btn"
                onClick={() => setViewOnceNoticeMsg(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <h4 id="wa-viewonce-modal-title" className="wa-viewonce-modal-title">
              View-Once Photo
            </h4>
            <div className="wa-viewonce-modal-subtitle">
              Sent by {viewOnceNoticeMsg.direction === "outbound" ? "You" : viewOnceNoticeMsg.sender_name || "Customer"} • {formatTime(viewOnceNoticeMsg.created_at)}
            </div>

            <div className="wa-viewonce-modal-body">
              <div className="wa-viewonce-modal-info-box">
                <i className="bi bi-shield-lock-fill text-success fs-3 me-3 flex-shrink-0"></i>
                <div className="wa-viewonce-modal-info-text">
                  <div className="wa-viewonce-modal-info-headline">Protected by Meta Privacy Protocol</div>
                  <div>
                    WhatsApp's View-Once feature allows photos and videos to be viewed only once directly inside the <strong>WhatsApp mobile application</strong>.
                  </div>
                </div>
              </div>

              <div className="wa-viewonce-modal-steps">
                <div className="wa-viewonce-step">
                  <i className="bi bi-phone me-2 text-primary"></i>
                  <span>To view this photo, open the WhatsApp chat on your registered mobile phone.</span>
                </div>
                <div className="wa-viewonce-step">
                  <i className="bi bi-info-circle me-2 text-muted"></i>
                  <span>Meta's WhatsApp Business Cloud API strictly protects View-Once media and does not permit third-party web apps to download or display it.</span>
                </div>
              </div>
            </div>

            <div className="wa-viewonce-modal-footer">
              <button
                type="button"
                className="btn btn-success wa-viewonce-btn-ok"
                onClick={() => setViewOnceNoticeMsg(null)}
              >
                OK, Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
